/**
 * Giving API query hooks are tested through integration tests in their
 * consuming components (e.g. NavBar renders useGetFundList via useAppIsReady
 * and useRouteSectionDeps).
 *
 * Cache hydration -> refetch for useGetFundList is tested in
 * NavBar/Giving/Giving.test.js ("refetch after hydration"). The test seeds
 * localStorage with stale fund list data plus network metadata and permissions
 * (so `enabled` is true from the first render), then asserts the missing fund
 * appears after the background refetch.
 *
 * @see NavBar/Giving/Giving.test.js - fund list hydration integration test
 * @see GroupsManagement/API/queries.test.js - groups hydration integration test
 * @see Utilities/ReactQuery/queryCache.js - CACHEABLE_KEYS includes givingKeys.getFundList()
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import { useGetPermissions } from "Flocknote/API/queries";
import { useNetworkMetadata } from "Flocknote/apiHooks";

import useFileMutation from "Utilities/Hooks/useFileMutation";
import { queryClient } from "Utilities/ReactQuery/queryClient";

import {
  deleteCta,
  getCampaigns,
  getCtas,
  getFundList,
  getFunds,
  getHappiSettings,
  getMainSettings,
  getMediaSettings,
  getOtherSettings,
  reorderCtas,
  setCta,
  setHappiSettings,
  setMainSettings,
  setMediaSettings,
  setOtherSettings,
  uploadPhoto,
} from "./fetch";
import givingKeys from "./keys";

const optimisticUpdate = async (queryKey, setFunction, payload) => {
  // get the call going
  const thePromise = setFunction(payload);

  // optimistic update
  const oldData = queryClient.getQueryData(queryKey) || {};
  queryClient.setQueryData(queryKey, { ...oldData, ...payload });

  // set the response data from the server when ready.
  const res = await thePromise;
  queryClient.setQueryData(queryKey, res);
  return res;
};

// Fires before permissions load so authorized users get data ASAP. If the
// user turns out to lack giving access, the single pre-permissions request
// may fail — retry is 0 until permissions arrive to avoid wasted retries.
// Once permissions resolve, enabled flips to false for unauthorized users.
const useGetFundList = () => {
  const [{ hasFundIt }] = useNetworkMetadata();
  const { data: permissions } = useGetPermissions();

  const canSeeGiving =
    !permissions ||
    permissions.canViewAllGivingData ||
    permissions.canAddGifts ||
    permissions.canSeeCampaignsNav ||
    permissions.canSeeMMGSetup;

  return useQuery({
    queryKey: givingKeys.getFundList(),
    queryFn: () => getFundList(),
    enabled: Boolean(hasFundIt) && Boolean(canSeeGiving),
    retry: permissions ? undefined : 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

const useGetFunds = () => {
  return useQuery({
    queryKey: givingKeys.getFunds(),
    queryFn: () => getFunds(),
  });
};

const useGetCampaigns = ({ enabled = true } = {}) => {
  return useQuery({
    queryKey: givingKeys.getCampaigns(),
    queryFn: () => getCampaigns(),
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

const useGetMainSettings = () => {
  return useQuery({
    queryKey: givingKeys.mainSettings(),
    queryFn: () => getMainSettings(),
  });
};

const useSetMainSettings = () => {
  const queryKey = givingKeys.mainSettings();
  return useMutation({
    mutationKey: queryKey,
    mutationFn: async (payload) =>
      optimisticUpdate(queryKey, setMainSettings, payload),
  });
};

const useGetMediaSettings = () => {
  return useQuery({
    queryKey: givingKeys.mediaSettings(),
    queryFn: () => getMediaSettings(),
  });
};

const useSetMediaSettings = () => {
  const queryKey = givingKeys.mediaSettings();
  return useMutation({
    mutationKey: queryKey,
    mutationFn: async (payload) => {
      queryClient.setQueryData(queryKey, payload);
      await setMediaSettings(payload);
      queryClient.invalidateQueries(queryKey);
    },
  });
};

const useReorderCtas = () => {
  const queryKey = givingKeys.cta();
  return useMutation({
    mutationKey: queryKey,
    mutationFn: async (payload) => {
      // get the call going
      const thePromise = reorderCtas(payload);

      // optimistic update
      const { ctas: oldData = [] } = queryClient.getQueryData(queryKey) || {};
      const ctas = payload.map((ID) =>
        oldData.find(({ ID: idToFind }) => idToFind === ID)
      );
      queryClient.setQueryData(queryKey, { ...oldData, ctas });

      // set the response data from the server when ready.
      const res = await thePromise;
      queryClient.setQueryData(queryKey, res);
      return res;
    },
  });
};

const useSetCta = (id) => {
  const queryKey = givingKeys.cta();
  const setFunction = useCallback((payload) => setCta(id, payload), [id]);

  return useMutation({
    mutationKey: queryKey,
    mutationFn: (payload) => optimisticUpdate(queryKey, setFunction, payload),
  });
};

const useDeleteCta = (id) => {
  const queryKey = givingKeys.cta();
  const setFunction = useCallback((payload) => deleteCta(id, payload), [id]);

  return useMutation({
    mutationKey: queryKey,
    mutationFn: (payload) => optimisticUpdate(queryKey, setFunction, payload),
  });
};

const useGetCtas = () => {
  return useQuery({
    queryKey: givingKeys.cta(),
    queryFn: () => getCtas(),
  });
};

const useUploadPhoto = () => {
  return useFileMutation({
    queryKey: givingKeys.uploadPhoto(),
    mutationFn: uploadPhoto,
  });
};

const useGetHappiSettings = () => {
  return useQuery({
    queryKey: givingKeys.happeSettings(),
    queryFn: () => getHappiSettings(),
  });
};

const useSetHappiSettings = () => {
  const queryKey = givingKeys.happeSettings();
  return useMutation({
    mutationKey: queryKey,
    mutationFn: async (payload) =>
      optimisticUpdate(queryKey, setHappiSettings, payload),
  });
};

const useGetOtherSettings = () => {
  return useQuery({
    queryKey: givingKeys.otherSettings(),
    queryFn: () => getOtherSettings(),
  });
};

const useSetOtherSettings = () => {
  const queryKey = givingKeys.otherSettings();
  return useMutation({
    mutationKey: queryKey,
    mutationFn: async (payload) =>
      optimisticUpdate(queryKey, setOtherSettings, payload),
  });
};

export {
  useGetFundList,
  useGetCampaigns,
  useGetMainSettings,
  useSetMainSettings,
  useGetMediaSettings,
  useSetMediaSettings,
  useReorderCtas,
  useSetCta,
  useGetCtas,
  useUploadPhoto,
  useGetHappiSettings,
  useSetHappiSettings,
  useGetOtherSettings,
  useSetOtherSettings,
  useGetFunds,
  useDeleteCta,
};
